import { motion } from 'framer-motion';
import { ShieldCheck, Check } from 'lucide-react';

const CHECKLIST = [
  'I have permission to record this session.',
  'I have permission to upload the session material for AI-assisted coaching support.',
  'I understand that the report provides coaching hypotheses only.',
  'I will validate insights with the client.',
  'I will protect client confidentiality.',
  'I will not use the report as diagnosis or psychological assessment.',
  'I will comply with applicable privacy and consent requirements in my location.',
];

export function SIConsentEthicsPage() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-success-10 rounded-xl">
            <ShieldCheck className="w-5 h-5 text-success-50" />
          </div>
          <h1 className="text-2xl font-bold text-gray-80">Consent & Ethics</h1>
        </div>
        <h2 className="text-lg font-semibold text-gray-60">Responsible AI-Assisted Coaching</h2>
      </motion.div>

      {/* Positioning */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <p className="text-sm text-gray-60 leading-relaxed mb-4">
          Session Intelligence™ is designed to <strong className="text-gray-80">support the human coach</strong>, not replace the coach. It provides coaching hypotheses based on client language.
        </p>
        <div className="bg-brand-5 border border-gray-20 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-2">Session Intelligence™ does NOT provide:</p>
          <ul className="grid sm:grid-cols-2 gap-1">
            {[
              'Therapy or therapeutic advice',
              'Psychological diagnosis or assessment',
              'Legal advice',
              'Medical advice',
              'Mental health advice',
              'A replacement for the coach\'s professional judgement',
            ].map(item => (
              <li key={item} className="flex items-center gap-2 text-xs text-gray-60">
                <div className="w-1 h-1 rounded-full bg-gray-40 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      {/* Coach Responsibility Checklist */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <h3 className="text-base font-semibold text-gray-80 mb-4">Coach Responsibility Checklist</h3>
        <p className="text-sm text-gray-50 mb-5 leading-relaxed">
          Before uploading client material, the coach should confirm:
        </p>
        <ul className="space-y-3">
          {CHECKLIST.map(item => (
            <li key={item} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-success-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-3 h-3 text-success-50" />
              </div>
              <span className="text-sm text-gray-60 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Sample Client Consent Language */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <h3 className="text-base font-semibold text-gray-80 mb-3">Sample Client Consent Language</h3>
        <p className="text-sm text-gray-50 mb-4">
          You can use or adapt this language when seeking client consent:
        </p>
        <blockquote className="bg-brand-5 border-l-4 border-success-40 pl-4 pr-4 py-4 rounded-r-2xl text-sm text-gray-60 leading-relaxed italic">
          "I sometimes use a secure coaching platform to transcribe and analyse sessions so I can better identify themes, patterns, and useful follow-up questions. The AI does not replace my judgement and is used only to support our coaching work. Are you comfortable with me recording and using today's session in that way?"
        </blockquote>
      </motion.div>

      {/* Upload consent gate preview */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl border border-gray-20 p-6"
      >
        <h3 className="text-base font-semibold text-gray-80 mb-3">Upload Consent Gate</h3>
        <p className="text-sm text-gray-50 mb-4 leading-relaxed">
          Before any upload, you will be asked to confirm:
        </p>
        <div className="bg-brand-5 border border-gray-20 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="w-4 h-4 rounded border-2 border-success-40 bg-white flex-shrink-0 mt-0.5" />
            <span className="text-xs text-gray-60 leading-relaxed">
              I confirm that I have permission to upload this session audio, transcript, or notes for transcription and AI-assisted coaching analysis. I understand that Session Intelligence™ provides coaching hypotheses only and does not provide therapy, diagnosis, medical advice, legal advice, psychological assessment, or mental health advice.
            </span>
          </label>
        </div>
      </motion.div>

      {/* Legal disclaimer */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-gray-10 rounded-2xl p-4"
      >
        <p className="text-xs text-gray-50 leading-relaxed">
          <strong className="text-gray-60">Legal Note:</strong> This is not legal advice. Coaches should seek their own legal guidance where appropriate, particularly regarding applicable privacy laws, recording consent requirements, and professional standards in their jurisdiction.
        </p>
      </motion.div>
    </div>
  );
}
